import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { resolveApiMessage, showToast } from '../../../helpers';
import { downloadBlob } from '../../../helpers/downloadHelper';
import {
  addMyMember,
  exportMyProgramReport,
  exportMyPrograms,
  getMyProgram,
  getMyPrograms,
  setMyMember,
  TAddPerson,
} from '../../../requests/customerPortal';
import {
  btnGhost,
  btnSecondary,
  Card,
  EmptyState,
  PageBody,
  PageHeader,
  Pill,
  rowClickClass,
  tableClass,
  tdClass,
  theadClass,
  thClass,
} from '../../Admin/ui';
import {
  AddPersonForm,
  BalanceBar,
  fmtDate,
  fmtMoney,
  fmtNum,
  LoadFailed,
  Stat,
  retryUnless4xx,
} from '../shared';

const ProgramDetail: React.FC<{ id: string }> = ({ id }) => {
  const [adding, setAdding] = useState(false);
  const [exporting, setExporting] = useState(false);
  const qc = useQueryClient();
  const onExportReport = async () => {
    setExporting(true);
    try {
      downloadBlob(await exportMyProgramReport(id), `program-report-${id}.csv`);
    } catch (e) {
      showToast(resolveApiMessage(e), 'error');
    } finally {
      setExporting(false);
    }
  };
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['my', 'program', id] });
    qc.invalidateQueries({ queryKey: ['my', 'programs'] });
  };
  const add = useMutation({
    mutationFn: (body: TAddPerson) => addMyMember(id, body),
    onSuccess: (r) => {
      showToast(r.reEnabled ? 'Member re-enabled' : 'Member added', 'success');
      setAdding(false);
      refresh();
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });
  const toggle = useMutation({
    mutationFn: (v: { memberId: string; enabled: boolean }) =>
      setMyMember(v.memberId, v.enabled),
    onSuccess: (r) => {
      showToast(r.enabled ? 'Member enabled' : 'Member disabled', 'success');
      refresh();
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });
  const {
    data,
    isPending: isLoading,
    error,
  } = useQuery({
    retry: retryUnless4xx,
    queryKey: ['my', 'program', id],
    queryFn: () => getMyProgram(id),
  });
  if (isLoading) return <LoadingSpinner />;
  if (error || !data) return <LoadFailed error={error} />;
  return (
    <>
      <PageHeader
        title={data.name ?? 'Program'}
        meta={
          <>
            {data.number} · <Pill value={data.status} /> ·{' '}
            {fmtDate(data.startDate)} – {fmtDate(data.endDate)}
          </>
        }
        back={{ to: PATH.adminPrograms, label: 'All programs' }}
        actions={
          <button
            className={btnSecondary}
            onClick={onExportReport}
            disabled={exporting}
            title="Balance, members, classes held and upcoming, registrations by month and by course, attended vs no-attend"
          >
            {exporting ? 'Exporting…' : 'Export report'}
          </button>
        }
      />
      <PageBody>
        <div className="grid grid-cols-3 gap-4">
          <Stat label="Invested" value={fmtMoney(data.invested)} />
          <Stat label="Used" value={fmtMoney(data.used)} />
          <Stat
            label="Remaining"
            value={fmtMoney(data.remaining)}
            caption={<BalanceBar used={data.used} invested={data.invested} />}
          />
        </div>
        <Card
          title={`Members · ${data.members.length}`}
          className="mt-6"
          right={
            !adding && !['Completed', 'Expired'].includes(data.status ?? '') ? (
              <button className={btnSecondary} onClick={() => setAdding(true)}>
                Add a member
              </button>
            ) : null
          }
        >
          {adding && (
            <div className="px-5 pt-4">
              <AddPersonForm
                label="Add one of your people to this program"
                busy={add.isPending}
                onSubmit={(body) => add.mutate(body)}
                onCancel={() => setAdding(false)}
              />
            </div>
          )}
          {data.members.length === 0 ? (
            <EmptyState
              title="No members yet"
              hint="Add your people here; they can then register for classes under this program."
            />
          ) : (
            <table className={tableClass}>
              <thead className={theadClass}>
                <tr>
                  <th className={thClass}>Name</th>
                  <th className={thClass}>Email</th>
                  <th className={thClass}>Status</th>
                  <th className={thClass} />
                </tr>
              </thead>
              <tbody>
                {data.members.map((m) => (
                  <tr key={m.id} className={m.enabled ? '' : 'opacity-50'}>
                    <td className={tdClass}>{m.name ?? '—'}</td>
                    <td className={tdClass}>{m.email ?? '—'}</td>
                    <td className={tdClass}>
                      <Pill
                        value={m.enabled ? m.status ?? 'Active' : 'Disabled'}
                      />
                    </td>
                    <td className={`${tdClass} text-right`}>
                      <button
                        className={btnGhost}
                        disabled={toggle.isPending}
                        onClick={() =>
                          toggle.mutate({ memberId: m.id, enabled: !m.enabled })
                        }
                      >
                        {m.enabled ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </PageBody>
    </>
  );
};

const AdminPrograms: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const id = params.get('id');
  const [exporting, setExporting] = useState(false);
  const onExport = async () => {
    setExporting(true);
    try {
      downloadBlob(await exportMyPrograms(), 'programs.csv');
    } catch {
      showToast('Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };
  const {
    data,
    isPending: isLoading,
    error,
  } = useQuery({
    retry: retryUnless4xx,
    queryKey: ['my', 'programs'],
    queryFn: getMyPrograms,
    enabled: !id,
  });

  if (id) return <ProgramDetail id={id} />;
  if (isLoading) return <LoadingSpinner />;
  if (error || !data) return <LoadFailed error={error} />;

  return (
    <>
      <PageHeader
        title="Programs & balances"
        meta="Every program (contract) on your account, with what has been invested, used and what remains."
        actions={
          <button
            className={btnSecondary}
            onClick={onExport}
            disabled={exporting || data.length === 0}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </button>
        }
      />
      <PageBody>
        <Card>
          {data.length === 0 ? (
            <EmptyState title="No programs on this account" />
          ) : (
            <table className={tableClass}>
              <thead className={theadClass}>
                <tr>
                  <th className={thClass}>Program</th>
                  <th className={thClass}>Status</th>
                  <th className={thClass}>Window</th>
                  <th className={`${thClass} text-right`}>Members</th>
                  <th className={`${thClass} text-right`}>Invested</th>
                  <th className={`${thClass} text-right`}>Used</th>
                  <th className={`${thClass} text-right`}>Remaining</th>
                </tr>
              </thead>
              <tbody>
                {data.map((p) => (
                  <tr
                    key={p.id}
                    className={rowClickClass}
                    onClick={() => setParams({ id: p.id })}
                  >
                    <td className={tdClass}>
                      <div className="font-medium text-gray-five">{p.name}</div>
                      <div className="text-xs text-gray-bold">{p.number}</div>
                    </td>
                    <td className={tdClass}>
                      <Pill value={p.status} />
                    </td>
                    <td className={`${tdClass} whitespace-nowrap`}>
                      {fmtDate(p.startDate)} – {fmtDate(p.endDate)}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      {fmtNum(p.memberCount)}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      {fmtMoney(p.invested)}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      {fmtMoney(p.used)}
                    </td>
                    <td className={`${tdClass} text-right font-semibold`}>
                      {fmtMoney(p.remaining)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
        <p className="mt-3 text-xs text-gray-bold">
          Balances are the figures on your program record. If something looks
          wrong, open a ticket and we will check it.
        </p>
      </PageBody>
    </>
  );
};

export default AdminPrograms;
